import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { TwoFactorLoginStep } from '@/components/TwoFactorLoginStep'
import { useAuth } from '@/hooks/useAuth'
import type { ApiError } from '@/lib/api'
import { AuthLayout } from '@/components/AuthLayout/AuthLayout'
import { ArrowRightIcon } from '@/pages/IndexPage/sorteeLanding/components/icons'
import styles from '@/components/AuthLayout/AuthForm.module.css'

function extractErrorMessage(error: unknown): string {
  const fallback = 'Não foi possível entrar. Verifique seus dados.'
  if (!error || typeof error !== 'object') return fallback
  const apiError = error as ApiError
  const details = apiError.details as
    | { message?: string; errors?: Record<string, string[]> }
    | undefined

  if (details?.errors) {
    const firstKey = Object.keys(details.errors)[0]
    const firstMessage = firstKey ? details.errors[firstKey]?.[0] : undefined
    if (firstMessage) return firstMessage
  }

  if (details?.message) return details.message
  return apiError.message || fallback
}

export function LoginPage() {
  const navigate = useNavigate()
  const { login, completeTwoFactorLogin } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  async function handlePasswordSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      const result = await login({ email, password })
      if (result.status === 'two_factor_required') {
        setChallengeToken(result.challengeToken)
        setTotpCode('')
        return
      }
      navigate('/home')
    } catch (error) {
      setErrorMessage(extractErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleTwoFactorSubmit() {
    if (!challengeToken) return
    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      await completeTwoFactorLogin({ challengeToken, code: totpCode })
      navigate('/home')
    } catch (error) {
      setErrorMessage(extractErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthLayout mode="login">
      <h1 className={styles.title}>
        {challengeToken ? 'Verificação em duas etapas' : 'Entrar'}
      </h1>
      <p className={styles.subtitle}>
        {challengeToken
          ? 'Abra o Google Authenticator e informe o código de 6 dígitos.'
          : 'Acesse sua conta para acompanhar suas campanhas.'}
      </p>

      {challengeToken ? (
        <TwoFactorLoginStep
          code={totpCode}
          onCodeChange={setTotpCode}
          onSubmit={() => {
            void handleTwoFactorSubmit()
          }}
          onBack={() => {
            setChallengeToken(null)
            setTotpCode('')
            setErrorMessage(null)
          }}
          isSubmitting={isSubmitting}
          errorMessage={errorMessage}
        />
      ) : (
        <form className={styles.form} onSubmit={handlePasswordSubmit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="login-email">
              E-mail
            </label>
            <input
              id="login-email"
              type="email"
              className={styles.input}
              placeholder="seu@email.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="login-password">
              Senha
            </label>
            <input
              id="login-password"
              type="password"
              className={styles.input}
              placeholder="********"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {errorMessage && (
            <div className={styles.error} role="alert">
              {errorMessage}
            </div>
          )}

          <div className={styles.row}>
            <label className={styles.checkboxWrapper}>
              <input
                type="checkbox"
                className={styles.checkbox}
                name="remember"
                defaultChecked
              />
              <span className={styles.checkboxLabel}>Lembrar de mim</span>
            </label>
            <Link to="/recuperar-senha" className={styles.forgotLink}>
              Esqueci minha senha
            </Link>
          </div>

          <button type="submit" className={styles.submit} disabled={isSubmitting}>
            {isSubmitting ? 'Entrando...' : 'Entrar'}
            <ArrowRightIcon className={styles.submitIcon} />
          </button>
        </form>
      )}

      {!challengeToken && (
        <div className={styles.footer}>
          <p className={styles.footerText}>
            Ainda não tem conta?
            <Link to="/cadastro" className={styles.signupLink}>
              Cadastre-se
            </Link>
          </p>
        </div>
      )}
    </AuthLayout>
  )
}
